import React, { useState, useCallback } from "react";
import Modal from "react-modal";
import { X, Flag, ThumbsDown, MessageSquare } from "lucide-react";

import styles from "./UserActivityModal.module.scss";

const TABS = {
  reported: "reported",
  notInterested: "notInterested",
};

const modalStyles = {
  content: {
    width: "100%",
    maxWidth: "720px",
    minHeight: "400px",
    maxHeight: "90vh",
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "12px",
    padding: "0",
    overflow: "hidden",
    background: "#1e293b",
    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.4)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(4px)",
    zIndex: 1050,
  },
};

const ContentCard = ({ item }) => {
  const thumbnailUrl =
    item.reels?.thumbnail?.uploadedImageUrl ||
    item.reels?.uploadedImageUrl ||
    item.thumbnail?.uploadedImageUrl;
  const mediaUrl = item.reels?.uploadedImageUrl || item.mediaUrl;
  const categories = item.category?.map((c) => c.label) || [];

  return (
    <article className={styles.card} aria-label={`Content: ${item.type} ${item.mediaType}`}>
      <div className={styles.cardMedia}>
        {item.mediaType === "video" && mediaUrl ? (
          <video
            src={mediaUrl}
            poster={thumbnailUrl}
            controls
            className={styles.video}
            aria-label="Video content"
          />
        ) : thumbnailUrl ? (
          <img src={thumbnailUrl} alt="" className={styles.thumbnail} />
        ) : (
          <div className={styles.mediaPlaceholder}>
            <span>{item.mediaType || "Media"}</span>
          </div>
        )}
      </div>
      <div className={styles.cardBody}>
        {categories.length > 0 && (
          <div className={styles.categories}>
            <span className={styles.categoryLabel}>Categories</span>
            <div className={styles.categoryTags}>
              {categories.slice(0, 7).map((label) => (
                <span key={label} className={styles.categoryTag}>
                  {label}
                </span>
              ))}
              {categories.length > 7 && (
                <span className={styles.categoryTag}>+{categories.length - 7}</span>
              )}
            </div>
          </div>
        )}
        {item.reason && (
          <div className={styles.reason}>
            <span className={styles.reasonLabel}>
              <MessageSquare size={12} aria-hidden />
              Reason
            </span>
            <p className={styles.reasonText}>{item.reason}</p>
          </div>
        )}
      </div>
    </article>
  );
};

const TabList = ({ activeTab, onTabChange, reportsCount, notInterestedCount }) => (
  <div
    className={styles.tabList}
    role="tablist"
    aria-label="Activity categories"
  >
    <button
      type="button"
      role="tab"
      id="tab-reported"
      aria-selected={activeTab === TABS.reported}
      aria-controls="panel-reported"
      tabIndex={activeTab === TABS.reported ? 0 : -1}
      className={`${styles.tab} ${activeTab === TABS.reported ? styles.tabActive : ""}`}
      onClick={() => onTabChange(TABS.reported)}
    >
      <Flag size={16} aria-hidden />
      <span className={styles.tabText}>Reported</span>
      <span className={styles.tabCount} aria-label={`${reportsCount} items`}>
        {reportsCount}
      </span>
    </button>
    <button
      type="button"
      role="tab"
      id="tab-notInterested"
      aria-selected={activeTab === TABS.notInterested}
      aria-controls="panel-notInterested"
      tabIndex={activeTab === TABS.notInterested ? 0 : -1}
      className={`${styles.tab} ${activeTab === TABS.notInterested ? styles.tabActive : ""}`}
      onClick={() => onTabChange(TABS.notInterested)}
    >
      <ThumbsDown size={16} aria-hidden />
      <span className={styles.tabText}>Not Interested</span>
      <span className={styles.tabCount} aria-label={`${notInterestedCount} items`}>
        {notInterestedCount}
      </span>
    </button>
  </div>
);

const TabPanel = ({ id, isActive, children }) => (
  <div
    role="tabpanel"
    id={id}
    aria-labelledby={`tab-${id.replace("panel-", "")}`}
    hidden={!isActive}
    className={styles.tabPanel}
  >
    {children}
  </div>
);

const UserActivityModal = ({
  isOpen,
  onClose,
  userName,
  reportsData = [],
  notInterestedData = [],
  loading = false,
}) => {
  const [activeTab, setActiveTab] = useState(TABS.reported);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const reportsCount = reportsData?.length || 0;
  const notInterestedCount = notInterestedData?.length || 0;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      contentLabel="User Activity"
      aria-modal="true"
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 id="activity-modal-title" className={styles.title}>
            Activity{userName ? ` – ${userName}` : ""}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <TabList
          activeTab={activeTab}
          onTabChange={handleTabChange}
          reportsCount={reportsCount}
          notInterestedCount={notInterestedCount}
        />

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <p>Loading activity...</p>
            </div>
          ) : (
            <>
              <TabPanel id="panel-reported" isActive={activeTab === TABS.reported}>
                {reportsCount > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {reportsData.map((item) => (
                      <ContentCard key={item._id} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <Flag size={40} aria-hidden />
                    <p>No reported content</p>
                  </div>
                )}
              </TabPanel>

              <TabPanel id="panel-notInterested" isActive={activeTab === TABS.notInterested}>
                {notInterestedCount > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {notInterestedData.map((item) => (
                      <ContentCard key={item._id} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <ThumbsDown size={40} aria-hidden />
                    <p>No not interested content</p>
                  </div>
                )}
              </TabPanel>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserActivityModal;
